import Logger from 'components/Common/Logger';
import { FLOW_QUERY_KEYS } from 'utils/constants/flow.constant';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';


/**
 * Hook to refresh flow report data by invalidating relevant queries
 * @param {Object} params - Parameters for refresh
 * @param {string} params.runId - Flow run ID
 * @param {string} params.orgId - Organization ID
 * @param {string} params.crId - Clean room ID
 * @param {string} params.selectedNode - Selected node ID (optional)
 * @param {string} params.flowId - Flow ID (optional, required if selectedNode is provided)
 * @returns {Function} refresh function
 */
export const useRefreshFlowReport = ({
  runId, orgId, crId, selectedNode, flowId,
}) => {
  const queryClient = useQueryClient();

  const refresh = useCallback(() => {
    try {
      // Invalidate and refetch flow run and nodes queries
      queryClient.invalidateQueries([FLOW_QUERY_KEYS.CR_FLOW_RUN, runId, orgId]);
      queryClient.invalidateQueries([FLOW_QUERY_KEYS.FLOW_NODE_OUTPUTS, crId, orgId]);
      // If a node is selected, also refresh node details
      if (selectedNode && flowId) {
        queryClient.invalidateQueries([
          FLOW_QUERY_KEYS.FETCH_NODE_DETAILS,
          orgId,
          flowId,
          selectedNode,
        ]);
        queryClient.invalidateQueries([
          FLOW_QUERY_KEYS.FLOW_NODE_RUN,
          orgId,
          crId,
          runId,
          selectedNode,
        ]);
      }
    }
    catch (error) {
      // Silently handle errors - the queries will handle their own error states
      Logger.error(error, 'Error refreshing flow data');
    }
  }, [queryClient, runId, orgId, crId, selectedNode, flowId]);

  return refresh;
};

